import toast from "react-hot-toast";

// ---------------------------------------------------------------------------
// Por qué el rollo no llega tal cual a la impresora
//
// El ancho del rollo (80mm / 58mm) viaja en `options.pageSize` y Electron lo
// traduce al tamaño real de página (`options.mediaSize`), de modo que el ticket
// se maqueta al ancho del rollo. El alto, en cambio, no lo pone nadie:
// electron-pos-printer usa la altura del contenido del ticket, una medida que no
// corresponde a ningún formulario del driver, así que la impresora la sustituye
// por su papel por defecto —A4— y el ticket sale encogido y centrado sobre el
// rollo. Por eso el arreglo está en la app de escritorio (agest-desktop): allí
// se pregunta a la impresora qué formularios declara y, si alguno es del ancho
// del rollo, se le pide ese. Esta parte solo tiene que mandar bien el `pageSize`
// y no estirar el ticket, porque cualquier ampliación aquí sale enorme.
// ---------------------------------------------------------------------------

/**
 * Los ajustes se leen en cada impresión y no al cargar el módulo: así un
 * cambio de impresora o de papel se nota sin reiniciar la aplicación.
 */
export function getTicketOptions() {
    const pageSize = localStorage.getItem('ticketSize') || '80mm';
    return {
        preview: localStorage.getItem('previewPrint') == 'true' || false,
        printBackground: true,
        // La librería lee `margins`, no `margin`: enviado así, el margen se
        // ignoraba y Chromium quitaba su propio margen por defecto.
        margins: { marginType: 'none' },
        copies: 1,
        printerName: localStorage.getItem('printer'),
        // Margen de seguridad antes de que el puente dé una impresión por
        // fallida. electron-pos-printer calcula el plazo con
        // timeOutPerLine * lineas + 200, y el payload son siempre 2 lineas: con
        // 400 solo daba 1s, menos de lo que tarda una impresora térmica que
        // estaba dormida. Con 4000 son 8,2s, de sobra para que arranque y poco
        // para que una impresora apagada no bloquee el siguiente sondeo.
        timeOutPerLine: 4000,
        silent: localStorage.getItem('silentPrint') == 'true' || false,
        pageSize, // ancho del rollo; la app de escritorio lo sustituye por el
                  // formulario que declare la impresora
    }
}

// Ajustes de la impresora de cocina (sección Comandas de Ajustes). Si el local
// no tiene impresora de comandas propia cae en la de tickets para que la comanda
// salga igualmente en algún sitio.
export function getComandaOptions() {
    const pageSize = localStorage.getItem('comandticketSize') || localStorage.getItem('ticketSize') || '80mm';
    return {
        preview: false,                    // la comanda va directa al papel
        printBackground: true,
        margins: { marginType: 'none' },
        copies: 1,
        printerName: localStorage.getItem('comandprinter') || localStorage.getItem('printer'),
        // Margen de seguridad antes de que el puente dé una impresión por fallida.
        // electron-pos-printer calcula el plazo con timeOutPerLine * lineas + 200, y
        // el payload son siempre 2 lineas: con 400 solo daba 1s.
        timeOutPerLine: 4000,
        // Impresión directa por defecto: el dispositivo de cocina no tiene a nadie
        // delante de la pantalla, así que una vista previa deja el papel parado.
        // El ajuste `comandsilentPrint` solo sirve para desactivarla.
        silent: localStorage.getItem('comandsilentPrint') != 'false',
        pageSize, // ancho del rollo; la app de escritorio lo sustituye por el
                  // formulario que declare la impresora
    }
}

export function generateTicketData(data) {
    let d = [
        {
            type: 'text',                                       // 'text' | 'barCode' | 'qrCode' | 'image' | 'table
            value: data,
            style: {}    // style for the text
        },
        {
            type: 'text',                                       // 'text' | 'barCode' | 'qrCode' | 'image' | 'table
            value: '<div></div>',
            style: { fontSize: '20px', textAlign: 'center', height: '60px' }    // style for the text
        },
    ]
    return d;
}


/**
 * electron-pos-printer da por fallada una impresión que solo va lenta: su
 * temporizador (timeOutPerLine * lineas + 200 ms) salta aunque el trabajo ya
 * esté en la cola de la impresora. Tratarlo como fallo hace que se reintente y
 * saquen dos copias.
 */
function isPrintTimeout(error) {
    return String(error?.message || error).includes('TimedOutError');
}

/**
 * Lo que dice el fallo, sin el ruido que mete Electron al cruzar el puente
 * ("Error invoking remote method 'print-ticket': ..."). Chromium da motivos
 * concretos —"Invalid printer settings", "Print job canceled"...— y son los que
 * hay que enseñar cuando una impresora no imprime.
 */
function printErrorReason(error) {
    const text = String(error?.message || error || '')
        .replace(/^Error invoking remote method '[^']+':\s*/, '')
        .trim();

    return text.length > 90 ? `${text.slice(0, 90)}…` : text;
}

/**
 * Imprime un documento ya generado en HTML y espera al puente de Electron.
 *
 * @param {string} data    HTML del ticket (ver `ticket.jsx`).
 * @param {object} options Ajustes de impresión; por defecto los del ticket de venta.
 * @returns {Promise<boolean>} Si el documento se ha enviado a la impresora.
 */
export async function printTicket(data, options = getTicketOptions()) {
    console.log('printTicket: llamado con', {
      printerName: options.printerName,
      silent: options.silent,
      pageSize: options.pageSize,
      dataLen: data.length,
    });
    const d = [
        {
            type: 'text',                                       // 'text' | 'barCode' | 'qrCode' | 'image' | 'table
            value: data,
            style: {}    // style for the text
        },
        {
            type: 'text',                                       // 'text' | 'barCode' | 'qrCode' | 'image' | 'table
            value: '<div></div>',
            style: { fontSize: '20px', textAlign: 'center', height: '60px' }    // style for the text
        },
    ]
    if (typeof window.electron?.printTicket !== "function") {
        toast.error('No se ha podido imprimir el ticket.', { id: 'print-error' });
        return false;
    }
    try {
        // ipcRenderer.invoke resuelve cuando Chromium da el trabajo por enviado.
        // Sin esperarlo, un fallo de impresión solo se ve como un rechazo sin
        // capturar y el documento se da por bueno igualmente.
        await window.electron.printTicket(d, options);
        console.log('printTicket: resuelto OK', options.printerName);
        return true;
    } catch (error) {
        if (isPrintTimeout(error)) {
            // El trabajo puede estar ya en la cola —Chromium solo ha tardado más
            // de lo que la librería aguanta—, así que se da por impreso para no
            // repetirlo en el siguiente sondeo. Pero avisar hace falta: si la
            // impresora no está, el fallo es completamente mudo y parece que el
            // ticket no se ha imprimir. El aviso es de "no confirmado", no de
            // error, porque puede que el papel haya salido.
            console.warn('printTicket: la impresora no ha confirmado en el plazo', options.printerName, error);
            toast('La impresora no ha confirmado la impresión.', { icon: '⚠', id: 'print-timeout' });
            return true;
        }

        // El aviso genérico no sirve de nada: lo que falla es Chromium, el
        // driver o el puente, y el mensaje lo dice. Se enseña recortado
        // porque va a un camarero y el detalle completo va a la consola.
        const reason = printErrorReason(error);
        console.error('printTicket:', error);
        toast.error(`No se ha podido imprimir el ticket. ${reason}`, { id: 'print-error' });
        return false;
    }
}

/**
 * Imprime una comanda de cocina en la impresora de comandas y por impresión
 * directa, sin vista previa. Devuelve si el papel se ha enviado.
 */
export function printComanda(data) {
    return printTicket(data, getComandaOptions());
}